import { type LoginFormData, type LoginFormErrors } from '../../../types/auth';

export interface ValidationRule<T> {
  validate: (value: T) => string | undefined;
}

/**
 * Authentication Validation Service
 * 
 * Handles all validation logic for authentication forms.
 * Follows single responsibility principle and is easily testable.
 */
export class AuthValidationService {
  private static readonly EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /**
   * Validates username field
   */
  private static validateUsername(username: string): string | undefined {
    if (!username || !username.trim()) {
      return 'Usuario es requerido';
    }

    if (username.trim().length < 3) {
      return 'Usuario debe tener al menos 3 caracteres';
    }

    return undefined;
  }

  /**
   * Validates password field
   */
  private static validatePassword(password: string): string | undefined {
    if (!password || !password.trim()) {
      return 'Contraseña es requerida';
    }

    return undefined;
  }

  /**
   * Validates complete login form
   */
  public static validateLoginForm(formData: LoginFormData): LoginFormErrors {
    const errors: LoginFormErrors = {};

    // Validate username
    const usernameError = this.validateUsername(formData.username);
    if (usernameError) {
      errors.username = usernameError;
    }

    // Validate password
    const passwordError = this.validatePassword(formData.password);
    if (passwordError) {
      errors.password = passwordError;
    }

    return errors;
  }

  /**
   * Validates a single field
   */
  public static validateField(field: keyof LoginFormData, value: string): string | undefined {
    switch (field) {
      case 'username':
        return this.validateUsername(value);
      case 'password':
        return this.validatePassword(value);
      default:
        return undefined;
    }
  }

  /**
   * Checks if form is valid (no errors)
   */
  public static isFormValid(errors: LoginFormErrors): boolean {
    return Object.keys(errors).length === 0;
  }

  /**
   * Gets user-friendly error message for authentication errors
   */
  public static getAuthErrorMessage(errorCode: string): string {
    switch (errorCode) {
      case 'INVALID_CREDENTIALS':
        return 'Usuario o contraseña incorrectos';
      case 'ACCOUNT_LOCKED':
        return 'Tu cuenta ha sido bloqueada. Contacta al administrador';
      case 'ACCOUNT_DISABLED':
        return 'Tu cuenta ha sido deshabilitada';
      case 'NETWORK_ERROR':
        return 'Error de conexión. Verifica tu conexión a internet';
      case 'SERVER_ERROR':
        return 'Error del servidor. Por favor contacta al administrador del sistema';
      default:
        return 'Ha ocurrido un error inesperado. Intenta nuevamente';
    }
  }

  /**
   * Validates email format only (for real-time validation)
   */
  public static isValidEmailFormat(email: string): boolean {
    return this.EMAIL_REGEX.test(email.trim());
  }

}